import { VISIBILITY_STATUS } from '@lighthouse/core'
import React, { forwardRef, Fragment, memo, useMemo } from 'react'

import { DEVELOPER } from '../../constants'
import { useDependentDataContext } from '../../contexts'
import type { VisibleDomProps } from '../VisibleDom'
import { VisibleDom } from '../VisibleDom'
import type { ContainerLayoutContextValue } from './Context'
import { ContainerLayoutProvider, useContainerLayoutContext, useFlowLayoutContext } from './Context'
import { DOM_DATA_LEVELS_NAME, DOM_DATA_NODE_NAME, DOM_DATA_PARENT_NAME } from './Interaction'
import {
    DOM_DATA_DISABLED,
    DOM_DATA_NO_DRAGGABLE,
    DOM_DATA_NODE_SCOPE,
    DOM_DATA_REAL_PARENT_ID,
    DOM_DATA_VIRTUAL
} from './Interaction/utils'
import type { FlowLayoutNodeProps } from './Node/Node'
import { Node } from './Node/Node'
import { NodeMask } from './Node/NodeMask'

interface Props extends FlowLayoutNodeProps {
    visibleParams?: VisibleDomProps
}

export const NodeRender = memo(
    forwardRef<HTMLDivElement, Props>((props, ref) => {
        const { data, style } = props
        const { selectedNodes, disabled: globalDisabled, useVisibilityFilter } = useFlowLayoutContext()
        const disabled = data.disabled || globalDisabled

        const { parentId, level } = useContainerLayoutContext()

        const dependentData = useDependentDataContext()

        const isSelected = selectedNodes?.some(item => item.scope === data.scope && item.id === data.id)
        const isContainer =
            data.type === 'formContainer' ||
            (data.type === 'container' && data.virtual) ||
            data.type === 'overlay' ||
            ((data.children ?? []).length > 0 && data.type === 'block')
        const context = useMemo<ContainerLayoutContextValue>(
            () => ({ parentId: data.id, level: level + 1, ...data.data }),
            [data.data, data.id, level]
        )

        const visibilityFilter = useVisibilityFilter(data.data?.visibility?.visibilityFilter)
        const isVisible = useMemo(() => visibilityFilter({ visibility: data.data?.visibility }), [data.data?.visibility, visibilityFilter])

        const mergeStyle: React.CSSProperties = useMemo(() => {
            return {
                ...style,
                touchAction: 'manipulation',
                zIndex: level,
                ...(isVisible === VISIBILITY_STATUS.HIDDEN ? { display: 'none' } : {})
            }
        }, [style, level, isVisible])

        if (isVisible === VISIBILITY_STATUS.UNMOUNT) {
            return null
        }

        return (
            <Node
                {...props}
                ref={ref}
                tabIndex={0}
                disabled={disabled}
                style={mergeStyle}
                {...{
                    [DOM_DATA_NODE_NAME]: data.id,
                    [DOM_DATA_NODE_SCOPE]: data.scope,
                    [DOM_DATA_LEVELS_NAME]: level,
                    [DOM_DATA_PARENT_NAME]: parentId || 'root',
                    [DOM_DATA_REAL_PARENT_ID]: data.realParentId,
                    [DOM_DATA_DISABLED]: disabled || undefined,
                    [DOM_DATA_VIRTUAL]: data.virtual || undefined,
                    [DOM_DATA_NO_DRAGGABLE]: data.disableDrag
                }}
                extra={
                    <>
                        {!data.virtual && !disabled && !isSelected && <NodeMask style={{ zIndex: 1 }} />}
                        {dependentData.userId === DEVELOPER && dependentData && (
                            <VisibleDom id={data.id} {...dependentData} filter={data.data?.visibility} />
                        )}
                    </>
                }
            >
                {isContainer && data.children && (
                    <ContainerLayoutProvider value={context}>
                        {data.children.map(item => (
                            <Fragment key={item.id}>
                                <NodeRender data={item} />
                            </Fragment>
                        ))}
                    </ContainerLayoutProvider>
                )}
            </Node>
        )
    })
)

NodeRender.displayName = 'LayoutEngineNodeRender'
